import { useState, useEffect, type FormEvent } from 'react';
import translations from '../../translations';
import FormGroup from '../generic/FormGroup';
import { useEnvironments } from '../admin/environments/EnvironmentsProvider';
import { ENVIRONMENT_CATEGORY_TYPES } from '../admin/environments/constants';
import type { ReleaseFilter } from '../../types/releases';
import CreatableSelect from 'react-select/creatable';
import { getSharedStyles } from '../../utils/select';

interface Props {
    onReleaseIdChanged: (id: string | null) => void;
    onReleaseNameChanged: (name: string | null) => void;
    onCommentChanged: (e: FormEvent<HTMLTextAreaElement>) => void;
    isThemeBuild: boolean;
}

type ReleaseOption = {
    value: string;
    label: string;
    __isNew__?: boolean;
};

const ActivateBuild = ({
    onReleaseIdChanged,
    onReleaseNameChanged,
    onCommentChanged,
    isThemeBuild,
}: Props) => {
    const { getReleases } = useEnvironments();
    const [releaseOptions, setReleases] = useState<ReleaseOption[]>([]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: Treat warnings as errors, fix later
    useEffect(() => {
        const loadReleases = async () => {
            const filter: ReleaseFilter = {
                environmentclassificationtypes: [
                    ENVIRONMENT_CATEGORY_TYPES.development,
                    ENVIRONMENT_CATEGORY_TYPES.test,
                ],
            };

            const releases = await getReleases(filter);

            const releaseOptions = releases?.map((release) => ({
                value: release.id,
                label: release.name,
            }));
            setReleases(releaseOptions);
        };

        loadReleases();
    }, []);

    const onReleaseChange = (release: ReleaseOption | null) => {
        if (!release) {
            onReleaseIdChanged(null);
            onReleaseNameChanged(null);
            return;
        }

        const { value, label, __isNew__ } = release;

        if (__isNew__) {
            onReleaseIdChanged(null);
            onReleaseNameChanged(label);
        } else if (value) {
            onReleaseIdChanged(value);
            onReleaseNameChanged(label);
        }
    };

    return (
        <>
            <p>
                {isThemeBuild
                    ? translations.ENVIRONMENT_build_subtext_theme
                    : translations.ENVIRONMENT_build_subtext}
            </p>

            <FormGroup
                label={translations.FLOW_add_to_release_label}
                htmlFor="add-to-release-input"
            >
                <CreatableSelect
                    styles={getSharedStyles<ReleaseOption>()}
                    name="add-to-release"
                    inputId="add-to-release-input"
                    isMulti={false}
                    placeholder="Create or search for a release"
                    options={releaseOptions}
                    onChange={onReleaseChange}
                    noOptionsMessage={() => null}
                    components={{
                        DropdownIndicator: () => null,
                        IndicatorSeparator: () => null,
                    }}
                />
            </FormGroup>

            <FormGroup label="Build notes:" htmlFor="build-notes">
                <textarea
                    id="build-notes"
                    className="form-control form-control-textarea"
                    onChange={onCommentChanged}
                    rows={3}
                    placeholder="Description"
                />
            </FormGroup>
        </>
    );
};

export default ActivateBuild;
